// components/NotificationBell/index.tsx
import React, { useState, useCallback, useEffect } from 'react';
import { Badge, Drawer, message as antdMessage } from 'antd';
import { BellOutlined } from '@ant-design/icons';
import { useNotifications } from '../../shared/hooks/useNotifications.tsx';
import { NotificationDropdown } from './NotificationDropdown';
import { useNotificationsStore } from '../../shared/stores/useNotificationsStore';
import { getAndSanitize } from '../../shared/utils/sanitization';
import { useTheme } from '../../contexts/ThemeContext';

export const NotificationBell: React.FC = () => {
  const { theme } = useTheme();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [resolvingId, setResolvingId] = useState<string | null>(null);

  const { markAsResolved } = useNotificationsStore();

  // Callback estable para abrir el drawer
  const handleOpenDrawer = useCallback(() => {
    setDrawerOpen(true);
  }, []);

  const { notifications, unreadCount, loading, refresh, clearAllPopups, NotificationStackComponent } = useNotifications(handleOpenDrawer);

  // Efecto para limpiar popups cuando se abre el drawer
  useEffect(() => {
    if (drawerOpen) {
      clearAllPopups();
    }
  }, [drawerOpen, clearAllPopups]);

  const handleResolve = async (notificationId: string) => {
    const adminId = getAndSanitize('userId');
    if (!adminId) {
      antdMessage.error('No se pudo obtener el ID del administrador');
      return;
    }

    try {
      setResolvingId(notificationId);
      await markAsResolved(notificationId, adminId);
      antdMessage.success('Notificación resuelta exitosamente');
    } catch (error) {
      console.error('[NotificationBell] Error resolving notification:', error);
      antdMessage.error('Error al resolver la notificación');
    } finally {
      setResolvingId(null);
    }
  };

  const handleRefresh = () => {
    refresh();
  };

  return (
    <>
      {/* Render popup notifications */}
      {NotificationStackComponent}

      {/* Bell icon */}
      <div
        onClick={handleOpenDrawer}
        style={{
          cursor: 'pointer',
          padding: '8px 12px',
          display: 'flex',
          alignItems: 'center',
          transition: 'all 0.3s',
          borderRadius: '8px',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = theme === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.05)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent';
        }}
      >
        <Badge
          count={unreadCount}
          offset={[-2, 2]}
          style={{
            backgroundColor: '#ff4d4f',
            boxShadow: theme === 'dark' ? '0 0 0 1px #141414' : '0 0 0 1px #fff',
          }}
        >
          <BellOutlined
            style={{
              fontSize: 16,
              color: '#8b8b8b',
            }}
          />
        </Badge>
      </div>

      {/* Drawer */}
      <Drawer
        title="Notificaciones"
        placement="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={450}
        styles={{
          body: {
            padding: 0,
          },
        }}
      >
        <NotificationDropdown
          notifications={notifications}
          loading={loading}
          onResolve={handleResolve}
          onRefresh={handleRefresh}
          resolvingId={resolvingId}
        />
      </Drawer>
    </>
  );
};

export default NotificationBell;
